import { useState, useRef, useEffect } from 'react';
import { searchAccounts } from '../services/CreatorService';

function AccountSearch({ value, onChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false); // true once a search has completed
  const debounceRef = useRef(null);

  // Clear internal state when the parent clears the selection
  useEffect(() => {
    if (!value) {
      setQuery('');
      setResults([]);
      setSearched(false);
    }
  }, [value]);

  function handleQueryChange(e) {
    const q = e.target.value;
    setQuery(q);
    setSearched(false);
    setResults([]);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (q.trim().length < 2) {
      setSearching(false);
      return;
    }

    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await searchAccounts(q.trim());
        setResults(res ?? []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
        setSearched(true);
      }
    }, 300);
  }

  function selectAccount(account) {
    onChange(account);
    setQuery('');
    setResults([]);
    setSearched(false);
  }

  function clearSelection() {
    onChange(null);
  }

  // Selected state
  if (value) {
    return (
      <div className="accs-selected">
        <div className="accs-selected-card">
          <div className="accs-selected-info">
            <span className="accs-selected-charname">
              {value.characterName || <span className="accs-no-char">No character</span>}
            </span>
            <span className="accs-selected-meta">
              {value.username} &middot; {value.email} &middot; UID {value.id}
            </span>
          </div>
          <button className="accs-clear-btn" type="button" onClick={clearSelection} aria-label="Clear selection">
            &times;
          </button>
        </div>
      </div>
    );
  }

  // Search state
  return (
    <div className="accs-root">
      <div className={`accs-input-wrap${searching ? ' accs-input-wrap--searching' : ''}`}>
        <svg className="accs-search-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.5"/>
          <line x1="10" y1="10" x2="14" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        <input
          className="accs-input"
          type="text"
          value={query}
          onChange={handleQueryChange}
          placeholder="Search by username, email, or UID…"
          autoComplete="off"
        />
        {searching && <span className="accs-spinner" aria-hidden="true" />}
      </div>

      {results.length > 0 && (
        <ul className="accs-results">
          {results.map(account => (
            <li key={account.id}>
              <button
                type="button"
                className="accs-result-card"
                onClick={() => selectAccount(account)}
              >
                <span className="accs-result-charname">
                  {account.characterName || <span className="accs-no-char">No character</span>}
                </span>
                <span className="accs-result-username">{account.username}</span>
                <span className="accs-result-divider">·</span>
                <span className="accs-result-email">{account.email}</span>
                <span className="accs-result-uid">UID {account.id}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {searched && !searching && results.length === 0 && query.trim().length >= 2 && (
        <div className="accs-no-results">No accounts found for &ldquo;{query}&rdquo;</div>
      )}
    </div>
  );
}

export { AccountSearch };
